import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getGigDrivers, getGigDriver, verifyGigDriver } from '../../services/api';
import { Search, Check, X, Eye, Clock, ShieldCheck, ShieldX, Bike, Car, MapPin, Phone, Mail, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { key: 'pending_all', label: 'Pendientes', color: 'text-amber-600' },
  { key: 'verified', label: 'Verificados', color: 'text-green-600' },
  { key: 'rejected', label: 'Rechazados', color: 'text-red-600' },
  { key: 'all', label: 'Todos', color: 'text-gray-600' },
];

const VEHICLE_LABELS = { moto: '🏍️ Moto', auto: '🚗 Auto', bicicleta: '🚲 Bicicleta' };
const VERIFICATION_BADGES = {
  pending: { label: 'Registro incompleto', bg: 'bg-gray-100 text-gray-600' },
  documents_submitted: { label: 'Docs enviados', bg: 'bg-amber-100 text-amber-700' },
  verified: { label: 'Verificado', bg: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rechazado', bg: 'bg-red-100 text-red-600' },
};

const GigDriversAdmin = () => {
  const [drivers, setDrivers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending_all');
  const [search, setSearch] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => { loadDrivers(); }, [activeTab, search]);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (activeTab === 'pending_all') params.verification = 'pending,documents_submitted';
      else if (activeTab !== 'all') params.verification = activeTab;
      if (search) params.search = search;
      const res = await getGigDrivers(params);
      setDrivers(res.data.drivers || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Error al cargar repartidores');
    } finally { setLoading(false); }
  };

  const openDetail = async (driver) => {
    setShowModal(true);
    setLoadingDetail(true);
    setVerifyNotes('');
    try {
      const res = await getGigDriver(driver.id);
      setSelectedDriver(res.data);
    } catch (e) {
      setSelectedDriver(driver);
    } finally { setLoadingDetail(false); }
  };

  const handleVerify = async (action) => {
    if (!selectedDriver) return;
    if (action === 'reject' && !verifyNotes.trim()) {
      toast.error('Escribe el motivo del rechazo');
      return;
    }
    setVerifying(true);
    try {
      await verifyGigDriver(selectedDriver.id, action, verifyNotes);
      toast.success(action === 'approve' ? 'Repartidor aprobado' : 'Repartidor rechazado');
      setShowModal(false);
      loadDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al verificar');
    } finally { setVerifying(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Repartidores enLocal</h1>
          <p className="text-slate-600 mt-2">Gestión de repartidores independientes de la flotilla gig</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATUS_TABS.map(tab => {
            const count = tab.key === 'all' ? total : drivers.filter(d => d.verification_status === tab.key).length;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`bg-white rounded-xl border p-4 text-left transition-all ${activeTab === tab.key ? 'border-teal-500 shadow-md' : 'border-slate-200 hover:border-slate-300'}`}>
                <p className="text-2xl font-bold text-slate-900">{tab.key === 'all' ? total : '—'}</p>
                <p className={`text-sm font-medium ${tab.color}`}>{tab.label}</p>
              </button>
            );
          })}
        </div>

        {/* Search + Tabs */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex gap-2">
            {STATUS_TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === tab.key ? 'bg-teal-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Buscar por nombre, email..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          </div>
        </div>

        {/* Driver List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-700"></div>
          </div>
        ) : drivers.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Bike className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay repartidores en esta categoría</p>
          </div>
        ) : (
          <div className="space-y-3">
            {drivers.map(driver => {
              const badge = VERIFICATION_BADGES[driver.verification_status] || VERIFICATION_BADGES.pending;
              return (
                <div key={driver.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-xl font-bold text-teal-700">
                        {(driver.full_name || 'R')[0].toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{driver.full_name}</h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                          <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{driver.email}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{driver.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{VEHICLE_LABELS[driver.vehicle_type] || driver.vehicle_type}</span>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badge.bg}`}>{badge.label}</span>
                      <button onClick={() => openDetail(driver)}
                        className="flex items-center gap-1 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium transition">
                        <Eye className="w-4 h-4" /> Ver
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                    <span>Registrado: {formatDate(driver.created_at)}</span>
                    <span>Entregas: {driver.total_deliveries || 0}</span>
                    <span>Ganado: ${(driver.total_earned || 0).toFixed(0)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail / Verify Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-slate-900">Verificar Repartidor</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>

            {loadingDetail ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : selectedDriver && (
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">Nombre completo</label>
                    <p className="text-slate-900 font-medium">{selectedDriver.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">Email</label>
                    <p className="text-slate-900">{selectedDriver.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">Teléfono</label>
                    <p className="text-slate-900">{selectedDriver.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">Vehículo</label>
                    <p className="text-slate-900">{VEHICLE_LABELS[selectedDriver.vehicle_type]}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">CLABE</label>
                    <p className="text-slate-900 font-mono">{selectedDriver.bank_clabe || 'Sin registrar'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">Banco</label>
                    <p className="text-slate-900">{selectedDriver.bank_name || 'Sin registrar'}</p>
                  </div>
                </div>

                {/* Documents */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">Documentos</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'ine_photo_url', label: 'INE Frente' },
                      { key: 'ine_back_url', label: 'INE Reverso' },
                      { key: 'license_photo_url', label: 'Licencia de Conducir' },
                      { key: 'selfie_url', label: 'Selfie de Verificación' },
                    ].map(doc => (
                      <div key={doc.key}>
                        <label className="block text-sm font-medium text-slate-500 mb-2">{doc.label}</label>
                        {selectedDriver[doc.key] ? (
                          <a href={selectedDriver[doc.key]} target="_blank" rel="noopener noreferrer"
                            className="block border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition">
                            <img src={selectedDriver[doc.key]} alt={doc.label}
                              className="w-full h-40 object-cover bg-slate-100" />
                          </a>
                        ) : (
                          <div className="h-40 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200">
                            <p className="text-sm text-slate-400">No enviado</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Selfie vs INE comparison */}
                {selectedDriver.ine_photo_url && selectedDriver.selfie_url && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-blue-900 mb-3">Comparación INE vs Selfie</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-blue-700 mb-1">INE Frente</p>
                        <img src={selectedDriver.ine_photo_url} alt="INE" className="w-full h-32 object-cover rounded-lg" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-700 mb-1">Selfie</p>
                        <img src={selectedDriver.selfie_url} alt="Selfie" className="w-full h-32 object-cover rounded-lg" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats */}
                {selectedDriver.earnings_stats && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-slate-900">{selectedDriver.earnings_stats.total_deliveries || 0}</p>
                      <p className="text-xs text-slate-500">Entregas</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-teal-700">${(selectedDriver.earnings_stats.total_earned || 0).toFixed(0)}</p>
                      <p className="text-xs text-slate-500">Ganado</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-amber-600">${(selectedDriver.earnings_stats.pending || 0).toFixed(0)}</p>
                      <p className="text-xs text-slate-500">Pendiente</p>
                    </div>
                  </div>
                )}

                {/* Verification notes */}
                {selectedDriver.verification_notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-amber-900">Nota anterior: {selectedDriver.verification_notes}</p>
                  </div>
                )}

                {/* Verify Actions */}
                {selectedDriver.verification_status !== 'verified' && (
                  <div className="border-t border-slate-200 pt-4 space-y-3">
                    <textarea
                      placeholder="Notas de verificación (obligatorio para rechazar)"
                      value={verifyNotes}
                      onChange={(e) => setVerifyNotes(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      rows={2}
                    />
                    <div className="flex gap-3">
                      <button onClick={() => handleVerify('approve')} disabled={verifying}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition disabled:opacity-50">
                        <ShieldCheck className="w-5 h-5" />
                        {verifying ? 'Procesando...' : 'Aprobar Repartidor'}
                      </button>
                      <button onClick={() => handleVerify('reject')} disabled={verifying}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition disabled:opacity-50">
                        <ShieldX className="w-5 h-5" />
                        {verifying ? 'Procesando...' : 'Rechazar'}
                      </button>
                    </div>
                  </div>
                )}

                {selectedDriver.verification_status === 'verified' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-900">Repartidor verificado</p>
                      <p className="text-sm text-green-700">Verificado el {formatDate(selectedDriver.verified_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default GigDriversAdmin;
